import { cn } from "@/lib/utils";
import { AlertCircle, type LucideIcon } from "lucide-react";
import type { FunctionComponent, SVGProps } from "react";

// BootsmanResult type definition (duplicated to avoid import issues)
export type BootsmanResult<T = unknown> =
  | {
      success: true;
      data: T;
      message?: string;
    }
  | {
      success: false;
      error: {
        code: string;
        message: string;
        details?: unknown;
      };
    };

interface ToolErrorProps {
  result: unknown;
  className?: string;
  /**
   * Icon to display with the error message
   * @default AlertCircle
   */
  icon?: LucideIcon | FunctionComponent<SVGProps<SVGSVGElement>>;
  /**
   * Size of the icon
   * @default "sm" (w-3 h-3)
   */
  iconSize?: "xs" | "sm" | "md" | "lg";
  /**
   * Text size for the error message
   * @default "xs"
   */
  textSize?: "xs" | "sm" | "base" | "lg";
  /**
   * Custom error message to display instead of extracting from result
   */
  message?: string;
  /**
   * Whether to show the icon
   * @default true
   */
  showIcon?: boolean;
}

// Type guard for BootsmanResult
export function isBootsmanResult(value: unknown): value is BootsmanResult {
  if (!value || typeof value !== "object") {
    return false;
  }

  const obj = value as Record<string, unknown>;

  if ("success" in obj && typeof obj.success === "boolean") {
    if (obj.success) {
      return "data" in obj;
    }
    return (
      "error" in obj &&
      typeof obj.error === "object" &&
      obj.error !== null &&
      "code" in obj.error &&
      "message" in obj.error
    );
  }

  return false;
}

export function ToolError({
  result,
  className,
  icon: Icon = AlertCircle,
  iconSize = "sm",
  textSize = "xs",
  message,
  showIcon = true,
}: ToolErrorProps) {
  const errorMessage =
    message ||
    (() => {
      if (isBootsmanResult(result) && !result.success) {
        return result.error.message;
      }

      // Fallback for non-BootsmanResult errors
      if (result && typeof result === "object" && "error" in result) {
        const error = (result as Record<string, unknown>).error;
        if (typeof error === "string") {
          return error;
        }

        if (typeof error === "object" && error && "message" in error) {
          return String(error.message);
        }
      }

      return "Tool execution failed";
    })();

  const iconSizeClasses = {
    xs: "w-2 h-2",
    sm: "w-3 h-3",
    md: "w-4 h-4",
    lg: "w-5 h-5",
  } as const;

  const textSizeClasses = {
    xs: "text-xs",
    sm: "text-sm",
    base: "text-base",
    lg: "text-lg",
  } as const;

  return (
    <div
      className={cn("flex items-center gap-2 text-muted-foreground", className)}
    >
      {showIcon && (
        <Icon
          className={cn(
            iconSizeClasses[iconSize],
            "flex-shrink-0 text-warning",
          )}
        />
      )}
      <span className={textSizeClasses[textSize]}>{errorMessage}</span>
    </div>
  );
}
